import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError, useAuth } from "@/auth";
import { ShoppingCart, Loader2, CheckCircle2, Package, Clock, Mail, Phone, X } from "lucide-react";
import { Link } from "react-router-dom";

const PLAN_LABELS = { "3m": "3 mois", "6m": "6 mois", "12m": "12 mois" };

const STATUS_STYLES = {
  pending:                       { label: "En attente",                   color: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  pending_payment_validation:    { label: "🟠 Paiement en vérification",  color: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  payment_validated:             { label: "✅ Paiement validé",            color: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  payment_rejected:              { label: "❌ Paiement refusé",            color: "text-red-400 border-red-500/40 bg-red-500/10" },
  paid:                          { label: "Payé",                        color: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  activated:                     { label: "✓ Licence activée",            color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  expired:                       { label: "Expirée",                      color: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10" },
  cancelled:                     { label: "Annulée",                      color: "text-red-400 border-red-500/40 bg-red-500/10" },
};

function ToolCard({ tool, onOrder, pendingOrder }) {
  const [plan, setPlan] = useState("3m");
  const hasPrices = ["3m","6m","12m"].some(k => tool[`price_${k}`] != null);
  const price = tool[`price_${plan}`];

  return (
    <div className="card-lhs p-5 flex flex-col gap-3" data-testid={`tool-${tool.slug}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
          {tool.logo_url
            ? <img src={tool.logo_url} alt={tool.name} className="w-10 h-10 object-contain" />
            : <Package className="w-6 h-6 text-orange-500" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-extrabold text-sm leading-tight">{tool.name}</div>
          <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{tool.description || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5" data-testid={`tool-${tool.slug}-plans`}>
        {["3m","6m","12m"].map(k => (
          <button key={k} onClick={() => setPlan(k)}
            className={`text-center p-2 rounded-lg border text-[11px] transition ${
              plan === k ? "border-orange-500 bg-orange-500/10" : "border-zinc-800 hover:border-zinc-600"
            }`}>
            <div className="font-mono-tech uppercase text-[9px] text-zinc-500">{PLAN_LABELS[k]}</div>
            <div className={`font-display font-extrabold text-sm mt-0.5 ${plan === k ? "text-orange-400" : "text-zinc-300"}`}>
              {tool[`price_${k}`] != null ? `$${tool[`price_${k}`]}` : "—"}
            </div>
          </button>
        ))}
      </div>

      {pendingOrder ? (
        <div className={`text-[11px] text-center rounded-lg border px-2 py-2 ${STATUS_STYLES[pendingOrder.status]?.color || STATUS_STYLES.pending.color}`}>
          <Clock className="w-3 h-3 inline mr-1" />
          {STATUS_STYLES[pendingOrder.status]?.label} · {PLAN_LABELS[pendingOrder.plan]} · <span className="font-mono-tech">{pendingOrder.order_number}</span>
        </div>
      ) : (
        <button
          data-testid={`tool-${tool.slug}-order`}
          disabled={!hasPrices || price == null}
          onClick={() => onOrder(tool, plan)}
          className="btn-orange rounded-lg py-2 text-xs uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40">
          <ShoppingCart className="w-4 h-4" />
          {hasPrices && price != null ? `Demander (${price}$)` : "Prix à venir"}
        </button>
      )}
    </div>
  );
}

export default function StorePage() {
  const [tools, setTools] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // modal state is now multi-step: { tool, plan, step: 'account' | 'payment' | 'done', tpUsername, tpEmail, method, reference, lastOrder }
  const [modal, setModal] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const [t, o, c] = await Promise.all([
      axios.get(`${API}/store/tools`),
      axios.get(`${API}/store/orders`),
      axios.get(`${API}/merchant/config`),
    ]);
    setTools(t.data); setOrders(o.data); setCfg(c.data);
  };
  useEffect(() => { load(); }, []);

  const onOrder = (tool, plan) => setModal({
    tool, plan, step: "account",
    tpUsername: "", tpEmail: "",
    method: "binance", reference: "",
  });

  const goPaymentStep = () => {
    if (!modal.tpUsername.trim() || !modal.tpEmail.trim()) {
      setMsg({ ok: false, text: "Veuillez renseigner le nom d'utilisateur ET l'e-mail du compte créé sur le site du logiciel tiers." });
      return;
    }
    setMsg(null);
    setModal({ ...modal, step: "payment" });
  };

  const submitOrder = async () => {
    if (!modal) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/store/orders`, {
        tool_id: modal.tool.id,
        plan: modal.plan,
        tp_username: modal.tpUsername.trim(),
        tp_email: modal.tpEmail.trim(),
        payment_method: modal.method || null,
        payment_reference: (modal.reference || "").trim() || null,
      });
      setModal({ ...modal, step: "done", lastOrder: data });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e) });
    } finally { setBusy(false); }
  };

  // Pending/active order index per tool
  const ACTIVE_STATUSES = ["pending", "pending_payment_validation", "payment_validated", "paid", "activated"];
  const activeByTool = {};
  for (const o of orders) {
    if (ACTIVE_STATUSES.includes(o.status) && !activeByTool[o.tool_id]) {
      activeByTool[o.tool_id] = o;
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="store-page">
      <div className="card-lhs p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
              <ShoppingCart className="w-6 h-6 text-orange-500" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Store</div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Acheter d'autres logiciels</h1>
              <p className="text-zinc-400 text-sm mt-1">30 outils GSM pros · L'administrateur traite votre commande en moins de 30 min</p>
            </div>
          </div>
          <Link to="/contact-revendeur" data-testid="store-contact"
                className="btn-orange rounded-lg px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2">
            <Phone className="w-4 h-4" /> Contacter l'administrateur
          </Link>
        </div>
      </div>

      {msg && (
        <div data-testid="store-msg" className={`text-sm px-3 py-2 rounded-lg border ${
          msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                 : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-tools">
        {tools.map(t => <ToolCard key={t.id} tool={t} onOrder={onOrder} pendingOrder={activeByTool[t.id]} />)}
      </div>

      {orders.length > 0 && (
        <div className="card-lhs p-5" data-testid="store-orders-list">
          <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-4">Mes commandes</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto scroll-thin">
            {orders.map(o => {
              const s = STATUS_STYLES[o.status] || STATUS_STYLES.pending;
              return (
                <div key={o.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-tech text-orange-400 text-xs">{o.order_number}</div>
                    <div className="font-semibold truncate">{o.tool_name}</div>
                    <div className="text-[11px] text-zinc-500">
                      {PLAN_LABELS[o.plan]} · {o.price} {o.currency} · {new Date(o.created_at).toLocaleString("fr-FR")}
                    </div>
                    {o.notes && <div className="text-[11px] text-zinc-400 mt-1 italic">Note admin : {o.notes}</div>}
                  </div>
                  <span className={`shrink-0 self-start sm:self-center text-[10px] px-2 py-1 rounded-full border font-mono-tech ${s.color}`}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Multi-step modal — account info → payment instructions → confirmation */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="order-modal">
          <div className="card-lhs max-w-lg w-full p-6 orange-glow my-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <Package className="w-5 h-5 text-orange-500 shrink-0" />
                <h3 className="font-display font-extrabold text-lg truncate">
                  {modal.step === "done" ? "Demande enregistrée" : `Achat — ${modal.tool.name}`}
                </h3>
              </div>
              <button onClick={() => setModal(null)} className="text-zinc-400 hover:text-white" data-testid="order-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* STEP 1: account info */}
            {modal.step === "account" && (
              <>
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-4 text-sm">
                  <b className="text-amber-300 block mb-1">⚠️ Avant de continuer</b>
                  <p className="text-zinc-300 text-xs leading-relaxed">
                    Vous devez d'abord créer un compte sur le <b>site officiel du logiciel {modal.tool.name}</b> que vous souhaitez acheter.
                    Après la création de votre compte, renseignez ci-dessous les informations utilisées sur ce logiciel.
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">Nom d'utilisateur du logiciel *</label>
                    <input data-testid="tp-username-input" className="input-lhs w-full mt-1" value={modal.tpUsername}
                      onChange={(e) => setModal({ ...modal, tpUsername: e.target.value })} placeholder={`Votre identifiant sur ${modal.tool.name}`} />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500">E-mail du compte du logiciel *</label>
                    <input data-testid="tp-email-input" type="email" className="input-lhs w-full mt-1" value={modal.tpEmail}
                      onChange={(e) => setModal({ ...modal, tpEmail: e.target.value })} placeholder={`Votre email sur ${modal.tool.name}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-zinc-500">Nom d'utilisateur LHSunlocker</label>
                      <input className="input-lhs w-full mt-1 opacity-70" readOnly value={user?.name || ""} />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-zinc-500">E-mail LHSunlocker</label>
                      <input className="input-lhs w-full mt-1 opacity-70" readOnly value={user?.email || ""} />
                    </div>
                  </div>
                </div>

                <div className="flex items-baseline justify-between border-t border-zinc-800 pt-3 mt-5">
                  <span className="text-zinc-500 text-sm">Formule</span>
                  <span className="font-semibold">{PLAN_LABELS[modal.plan]}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-zinc-500 text-sm">Montant</span>
                  <span className="font-display font-extrabold text-2xl text-orange-500">${modal.tool[`price_${modal.plan}`]}</span>
                </div>

                {msg && !msg.ok && (
                  <div className="text-red-400 text-xs mt-3 bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2">{msg.text}</div>
                )}

                <div className="flex gap-2 mt-5">
                  <button onClick={() => setModal(null)} className="flex-1 border border-zinc-700 hover:border-zinc-500 rounded-lg py-2 text-sm">Annuler</button>
                  <button onClick={goPaymentStep} data-testid="order-next-step"
                    className="btn-orange flex-1 rounded-lg py-2 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                    Continuer vers le paiement
                  </button>
                </div>
              </>
            )}

            {/* STEP 2: payment instructions + reference */}
            {modal.step === "payment" && (
              <>
                <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-4 mb-4 text-sm">
                  <b className="text-orange-300 block mb-1">💳 Paiement de votre licence</b>
                  <p className="text-zinc-300 text-xs leading-relaxed">
                    Choisissez un moyen de paiement et contactez l'administrateur LHSunlocker ci-dessous.
                    Après avoir effectué le paiement, renseignez la référence et cliquez sur "J'ai effectué le paiement".
                  </p>
                </div>

                {/* Admin contacts */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  <a href={`https://wa.me/${(cfg?.admin_whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                    data-testid="admin-whatsapp"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/20">
                    <Phone className="w-3.5 h-3.5" /> WhatsApp : {cfg?.admin_whatsapp || "—"}
                  </a>
                  <a href={`https://t.me/${(cfg?.admin_telegram || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                    data-testid="admin-telegram"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-500/10 border border-sky-500/40 text-sky-300 text-xs hover:bg-sky-500/20">
                    <Mail className="w-3.5 h-3.5" /> Telegram : {cfg?.admin_telegram || "—"}
                  </a>
                </div>

                {/* Payment method radios */}
                <div className="space-y-2 mb-4">
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500">Moyen de paiement</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
                    {[
                      { id: "binance", label: "Binance Pay", hint: cfg?.binance_pay_id },
                      { id: "airtel", label: "Airtel Money", hint: cfg?.airtel_number },
                      { id: "orange", label: "Orange Money", hint: cfg?.orange_number },
                      { id: "visa", label: "Visa / Carte bancaire", hint: "Via admin" },
                    ].map((m) => (
                      <button key={m.id} onClick={() => setModal({ ...modal, method: m.id })}
                        data-testid={`method-${m.id}`}
                        className={`text-left p-3 rounded-lg border text-xs transition ${
                          modal.method === m.id ? "border-orange-500 bg-orange-500/10" : "border-zinc-800 hover:border-zinc-600"
                        }`}>
                        <div className="font-semibold">{m.label}</div>
                        <div className="text-[10px] text-zinc-500 font-mono-tech mt-1 truncate">{m.hint || "—"}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500">Référence / preuve de paiement</label>
                  <input data-testid="payment-reference-input" className="input-lhs w-full mt-1" value={modal.reference}
                    onChange={(e) => setModal({ ...modal, reference: e.target.value })}
                    placeholder="Ex: Numéro de transaction, dernier message WhatsApp…" />
                </div>

                <div className="flex items-baseline justify-between border-t border-zinc-800 pt-3 mt-5">
                  <span className="text-zinc-500 text-sm">Total à payer</span>
                  <span className="font-display font-extrabold text-2xl text-orange-500">${modal.tool[`price_${modal.plan}`]}</span>
                </div>

                {msg && !msg.ok && (
                  <div className="text-red-400 text-xs mt-3 bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2">{msg.text}</div>
                )}

                <div className="flex gap-2 mt-5">
                  <button onClick={() => setModal({ ...modal, step: "account" })}
                    className="flex-1 border border-zinc-700 hover:border-zinc-500 rounded-lg py-2 text-sm">Retour</button>
                  <button onClick={submitOrder} disabled={busy} data-testid="order-submit"
                    className="btn-orange flex-1 rounded-lg py-2 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    J'ai effectué le paiement
                  </button>
                </div>
              </>
            )}

            {/* STEP 3: confirmation */}
            {modal.step === "done" && modal.lastOrder && (
              <>
                <div className="text-center my-6">
                  <div className="inline-block px-5 py-2 rounded-full bg-amber-500 text-black font-black uppercase tracking-widest text-xs mb-3">
                    🟠 Paiement en attente de validation
                  </div>
                  <p className="text-zinc-300 text-sm">
                    Votre demande d'activation <b className="text-orange-400 font-mono-tech">{modal.lastOrder.order_number}</b> a été transmise à l'administrateur LHSunlocker.
                  </p>
                  <p className="text-zinc-500 text-xs mt-3">
                    Après vérification du paiement et activation, vous recevrez :
                    <br/>• un e-mail branded de preuve d'activation
                    <br/>• une entrée dans <b>Mes licences tierces</b>
                  </p>
                </div>
                <Link to="/mes-licences-tierces" onClick={() => setModal(null)} data-testid="go-to-licenses"
                  className="btn-orange w-full rounded-lg py-2 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                  Voir mes licences tierces
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
